import React, { useMemo } from 'react';
import { Box, IconButton, Tooltip } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { format } from 'sql-formatter';

interface SqlPreviewProps {
  sql: string;
  maxHeight?: number;
}

const SqlPreview: React.FC<SqlPreviewProps> = ({ sql, maxHeight = 200 }) => {
  const [copied, setCopied] = React.useState(false);

  const formatted = useMemo(() => {
    try {
      return format(sql, {
        language: 'sql',
        tabWidth: 2,
        keywordCase: 'upper',
        linesBetweenQueries: 2,
      });
    } catch {
      return sql;
    }
  }, [sql]);

  const handleCopy = () => {
    navigator.clipboard.writeText(formatted).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <Box sx={{ position: 'relative' }}>
      <Tooltip title={copied ? 'Copied!' : 'Copy SQL'}>
        <IconButton
          size="small"
          onClick={handleCopy}
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            zIndex: 1,
            color: 'grey.400',
            '&:hover': { color: 'grey.200' },
          }}
        >
          <ContentCopyIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <SyntaxHighlighter
        language="sql"
        style={vscDarkPlus}
        customStyle={{
          margin: 0,
          borderRadius: 4,
          fontSize: 12,
          maxHeight,
          overflow: 'auto',
        }}
        showLineNumbers
        lineNumberStyle={{ color: 'var(--ink-2)', fontSize: 11, minWidth: '2em' }}
      >
        {formatted}
      </SyntaxHighlighter>
    </Box>
  );
};

export default SqlPreview;
